/**
 * career-values-cardgame — the Werte-Kartenspiel (W1 redesign §3, V2), route
 * `/workshops/values`.
 *
 * A deterministic, keyboard-accessible reduction game: shuffle 62 → pairwise
 * »Was ist dir wichtiger?« down to 8 → optional bounded rescue/swap → pick 1
 * Lebenswert + 3 Superkräfte (the remaining 4 are Basiswerte) → confirm → commit.
 *
 * PERMANENTLY AI-FREE (Q7): the entire reducer runs client-side via the pure engine
 * in `@career/definition-v2` — NO model call, NO hint, ever. The pyramid is the one
 * artifact guaranteed free of AI influence. Only two things touch the server: an
 * OPTIONAL resume snapshot (persisted at phase/round boundaries) and the committing
 * pyramid. Re-running later forms a NEW pyramid version (supersede-by-latest, Q6);
 * nothing here assumes one pyramid per user. Focused disclosure: one decision on
 * screen at a time; all chrome flows through t(); card content is domain data.
 */

import {
  KitElement,
  signal,
  effect,
  type ActionMap,
  type KitContext,
  type ReadonlySignal,
  type Router,
  type Signal,
  type ViewModel,
  type Urn,
} from "@platform/web-kit"
import { classMap, html, nothing, type TemplateResult } from "@platform/web-kit/render"
import { nextStep } from "../lib/next-step"
import { spineDecl, toNextStep } from "../golden-path"
// Deep imports keep the browser bundle ArkType-free: `value-game` (pure reducer)
// and `value-card-catalog` (pure data) carry no domain-compile side effects — the
// same "no arktype in the browser bundle" discipline @career/cv-render-v2 uses. The
// domain SHAPE types come type-only from the barrel (erased at build).
import {
  newValueGame,
  currentPair,
  chooseWinner,
  rescueCandidates,
  unsureCandidates,
  swapCard,
  finishRefine,
  keptCards,
  pickLebenswert,
  toggleSuperkraft,
  confirmSuperkraefte,
  backToSuperkraefte,
  basiswerteIds,
  buildPyramid,
  roundPairCount,
  PYRAMID_SIZE,
  PYRAMID_SHAPE,
} from "@career/definition-v2/value-game"
import {
  VALUE_COLOUR_FAMILIES,
  valueCardById,
  type ValueCard,
  type ColourFamily,
} from "@career/definition-v2/value-card-catalog"
import type { ValueGameSnapshot, ValuePyramid } from "@career/definition-v2"
import { RouterToken, I18nToken, SessionToken, apiGet, apiPost, isSessionExpired } from "../services"
import { createCardgameDraftStore, type CardgameDraftStore } from "../lib/cardgame-draft"
import type { ShellSession } from "../domain"
import type { CareerI18n, CareerKey } from "../../i18n"

/** The card-game read model the api returns (`GET /workshops/values/cardgame`). */
interface ValueGameView {
  snapshot: ValueGameSnapshot | null
  pyramid: ValuePyramid | null
  pyramidCount: number
}

/** Colour-family display: the German colour word + the swatch class. Labels
 *  (Ordnung · Sicherheit …) come straight from the catalog data. */
const FAMILY_WORD: Record<ColourFamily, CareerKey> = {
  blau: "valuecard.family.blau",
  gruen: "valuecard.family.gruen",
  gelb: "valuecard.family.gelb",
  rot: "valuecard.family.rot",
}
const now = (): string => new Date().toISOString()

/** How long the user may dwell on ONE unchanged pair before the gentle support line fades in. */
const HINT_DELAY_MS = 5000

/**
 * Which »Leben voller / voll mit …« template reads naturally for this pair. Every value
 * noun in the frozen catalog is a single abstract noun, for which »voller {Wert}« is
 * idiomatic — so this returns false for today's cards. A multi-word name (none exist yet)
 * would read better with »voll mit«; the rule is mechanical, no model call (the pyramid
 * stays permanently AI-free, Q7). One connector for the whole sentence.
 */
function fullnessNeedsMit(...names: string[]): boolean {
  return names.some((n) => /[\s-]/.test(n.trim()))
}

/** The visible step path (redesign §3): the five phases the game walks through, in
 *  order. Each carries its short stepper label; the guide text keys off the phase. */
type GamePhase = ValueGameSnapshot["phase"]
const STEP_PATH: ReadonlyArray<{ phase: GamePhase; label: CareerKey }> = [
  { phase: "reduce", label: "valuecard.step.reduce" },
  { phase: "refine", label: "valuecard.step.refine" },
  { phase: "lebenswert", label: "valuecard.step.lebenswert" },
  { phase: "superkraefte", label: "valuecard.step.superkraefte" },
  { phase: "confirm", label: "valuecard.step.confirm" },
]

/** The deck-size milestones of the reduction journey (62 › 31 › 16 › 8) — derived
 *  from the deck the game started with, halved (ceil) down to the pyramid size. Pure
 *  presentation: it mirrors how `chooseWinner` folds each round's winners forward. */
function reduceMilestones(start: number): number[] {
  const out = [start]
  let n = start
  while (n > PYRAMID_SIZE) {
    n = Math.max(PYRAMID_SIZE, Math.ceil(n / 2))
    out.push(n)
  }
  return out
}

export class ValuesCardgameVm implements ViewModel {
  /** The server view — the committed pyramid + history count (read once, refreshed on commit). */
  readonly view: Signal<ValueGameView | null> = signal<ValueGameView | null>(null)
  /** The ACTIVE local game snapshot (null = not playing → intro / done). The client
   *  reducer is authoritative for responsiveness; the server snapshot only resumes. */
  readonly game: Signal<ValueGameSnapshot | null> = signal<ValueGameSnapshot | null>(null)
  readonly loaded: Signal<boolean> = signal(false)
  readonly error: Signal<string | null> = signal<string | null>(null)
  readonly busy: Signal<boolean> = signal(false)
  /** Refine-stage: the kept card selected to be swapped out (null = none). */
  readonly swapOut: Signal<string | null> = signal<string | null>(null)
  /** Reduce-stage: »unsicher« is armed — the next keep sets the OTHER card aside to
   *  rescue later (the loser goes to `maybe`, not `out`). Local UI state only; the
   *  pure reducer is untouched. Toggled by the on-screen button OR the `U` key. */
  readonly unsureArmed: Signal<boolean> = signal(false)
  /** The delayed support line beneath the question is showing — armed 5s after landing on a
   *  pair, reset the moment the pair changes. Pure UI nudge; the reducer is untouched (AI-free). */
  readonly hintVisible: Signal<boolean> = signal(false)
  readonly renderDeps: ReadonlyArray<ReadonlySignal<unknown>>
  readonly t: CareerI18n["t"]

  /** Per-user local draft — the run's insurance against a reload / session-expiry re-auth. */
  private readonly draft: CardgameDraftStore
  /** The pair the hint timer is currently counting for (`round:pairIndex`; null off `reduce`). */
  private hintPairKey: string | null = null
  private hintTimer: ReturnType<typeof setTimeout> | null = null

  constructor(private readonly router: Router, i18n: CareerI18n, session: ShellSession) {
    this.t = i18n.t
    this.draft = createCardgameDraftStore(session.user?.sub ?? null)
    this.renderDeps = [this.view, this.game, this.loaded, this.error, this.busy, this.swapOut, this.unsureArmed, this.hintVisible]
  }

  attach(): () => void {
    void this.refresh()
    // The delayed-hint clock: whenever the active reduce pair changes (a decision, a new round,
    // or leaving reduce), reset it — so the hint only ever rewards genuine dwell on ONE pair.
    const stopHint = effect([this.game], () => {
      const snap = this.game.value
      const key = snap && snap.phase === "reduce" ? `${snap.round}:${snap.pairIndex}` : null
      if (key === this.hintPairKey) return
      this.hintPairKey = key
      this.resetHintTimer(key !== null)
    })
    return () => {
      stopHint()
      this.clearHintTimer()
    }
  }

  private clearHintTimer(): void {
    if (this.hintTimer !== null) {
      clearTimeout(this.hintTimer)
      this.hintTimer = null
    }
  }

  /** Hide the hint now; when `arm`, fade it back in after {@link HINT_DELAY_MS} of dwell. */
  private resetHintTimer(arm: boolean): void {
    this.clearHintTimer()
    this.hintVisible.set(false)
    if (arm && typeof setTimeout === "function") {
      this.hintTimer = setTimeout(() => this.hintVisible.set(true), HINT_DELAY_MS)
    }
  }

  /** Set the active game AND mirror it into the local draft (or clear the draft on `null`). The
   *  ONE seam every mutation flows through, so a reload/re-auth always finds the freshest state. */
  private setGame(next: ValueGameSnapshot | null): void {
    this.game.set(next)
    if (next) this.draft.save(next)
    else this.draft.clear()
  }

  private async refresh(): Promise<void> {
    // Read the LOCAL draft first — it must be able to rescue the run even if the server is
    // unreachable or the session just expired (the exact data-loss the operator hit).
    const local = this.draft.load()
    try {
      const view = await apiGet<ValueGameView>("/api/workshops/values/cardgame")
      this.view.set(view)
      const serverSnap = view.snapshot && !view.pyramid ? view.snapshot : null
      const resume = this.pickResume(local, serverSnap)
      if (resume && (!view.pyramid || resume.updatedAt > view.pyramid.formedAt)) {
        // Resume the freshest in-progress game; keep the draft in step with what we chose.
        this.game.set(resume)
        this.draft.save(resume)
      } else if (local && view.pyramid && local.updatedAt <= view.pyramid.formedAt) {
        // A committed pyramid supersedes a stale local draft → drop it so »fertig« shows cleanly.
        this.draft.clear()
      }
    } catch (e) {
      // Offline / session expired: the server view is gone, but a local draft still rescues the
      // run — restore it. A 401 is owned by the shell's re-auth prompt, so never ALSO show a raw
      // surface error for it (the operator-flagged »bare 401«); other failures with no draft do.
      if (local) this.game.set(local)
      if (!local && !isSessionExpired(e)) this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.loaded.set(true)
    }
  }

  /** Pick the run to resume: the newer of the local draft and the server snapshot (tie → local,
   *  which is the freshest a mid-game reload could hold). Null when neither exists. */
  private pickResume(local: ValueGameSnapshot | null, server: ValueGameSnapshot | null): ValueGameSnapshot | null {
    if (local && server) return local.updatedAt >= server.updatedAt ? local : server
    return local ?? server
  }

  /** Persist the resume snapshot (fire-and-forget) at phase/round boundaries only —
   *  the local reducer stays responsive; a resume on another device continues. */
  private persist(prev: ValueGameSnapshot, next: ValueGameSnapshot): void {
    if (next.phase === prev.phase && next.round === prev.round) return
    void apiPost("/api/workshops/values/cardgame/progress", { snapshot: next }).catch(() => {})
  }

  /** Apply a pure-engine transition to the active game + persist on boundaries. */
  private advance(fn: (snap: ValueGameSnapshot) => ValueGameSnapshot): void {
    const prev = this.game.value
    if (!prev) return
    const next = fn(prev)
    if (next === prev) return
    this.setGame(next)
    this.persist(prev, next)
  }

  startNew(): void {
    this.error.set(null)
    this.swapOut.set(null)
    this.unsureArmed.set(false)
    const fresh = newValueGame(crypto.randomUUID(), now())
    this.setGame(fresh)
    void apiPost("/api/workshops/values/cardgame/progress", { snapshot: fresh }).catch(() => {})
  }

  /** Step back from Superkräfte to the Lebenswert pick (drives the confirm-screen »Zurück«). */
  backToLebenswert(): void {
    const snap = this.game.value
    if (!snap) return
    this.setGame({ ...snap, phase: "lebenswert" })
  }

  choose(winner: "a" | "b", unsure: boolean): void {
    this.advance((s) => chooseWinner(s, winner, unsure, now()))
  }

  /** Keep a side, honouring the armed »unsicher« flag — then disarm. This is the one
   *  path the card click AND the keyboard (←/→, 1/2) go through in the reduce phase. */
  chooseSide(winner: "a" | "b"): void {
    const unsure = this.unsureArmed.value
    this.choose(winner, unsure)
    if (unsure) this.unsureArmed.set(false)
  }

  toggleUnsure(): void {
    this.unsureArmed.set(!this.unsureArmed.value)
  }

  setUnsure(on: boolean): void {
    this.unsureArmed.set(on)
  }

  selectSwapOut(cardId: string): void {
    this.swapOut.set(this.swapOut.value === cardId ? null : cardId)
  }

  swapIn(rescueId: string): void {
    const dropId = this.swapOut.value
    if (!dropId) return
    this.advance((s) => swapCard(s, rescueId, dropId, now()))
    this.swapOut.set(null)
  }

  finishRefine(): void {
    this.advance((s) => finishRefine(s, now()))
  }

  pickLebenswert(cardId: string): void {
    this.advance((s) => pickLebenswert(s, cardId, now()))
  }

  toggleSuperkraft(cardId: string): void {
    this.advance((s) => toggleSuperkraft(s, cardId, now()))
  }

  confirmSuperkraefte(): void {
    this.advance((s) => confirmSuperkraefte(s, now()))
  }

  backToSuperkraefte(): void {
    this.advance((s) => backToSuperkraefte(s, now()))
  }

  async commit(): Promise<void> {
    const snap = this.game.value
    if (!snap || this.busy.value) return
    this.busy.set(true)
    this.error.set(null)
    try {
      const pyramid = buildPyramid(snap, now())
      const view = await apiPost<ValueGameView>("/api/workshops/values/cardgame/pyramid", { slots: pyramid.slots })
      this.view.set(view)
      // Committed — the run is safely server-side now, so drop the local draft.
      this.setGame(null)
    } catch (e) {
      this.error.set(e instanceof Error ? e.message : String(e))
    } finally {
      this.busy.set(false)
    }
  }

  go(url: string): void {
    void this.router.navigate(url)
  }
}

export class CareerValuesCardgame extends KitElement<ValuesCardgameVm> {
  protected createViewModel(ctx: KitContext): ValuesCardgameVm {
    return new ValuesCardgameVm(ctx.inject(RouterToken), ctx.inject(I18nToken), ctx.inject(SessionToken))
  }

  // ── Keyboard play (operator directive: fully playable without a mouse) ─────────
  // A single host-level keydown listener drives the whole game: ←/→ or 1/2 choose a
  // pair winner and `U` arms »unsicher« in the reduce phase; arrow keys rove focus
  // across the card tiles in the pick phases (Enter/Space then fire the tile's native
  // click); Enter commits and Escape steps back on the confirm screen. Real <button>s
  // already carry Enter/Space + focus rings; this only adds the game-shaped shortcuts.

  override connectedCallback(): void {
    super.connectedCallback()
    // Document-level so the game answers the keyboard immediately — the player never
    // has to focus a card first. Removed on disconnect (this surface is one route).
    ;(this.ownerDocument ?? document).addEventListener("keydown", this.onKeydown)
  }

  override disconnectedCallback(): void {
    ;(this.ownerDocument ?? document).removeEventListener("keydown", this.onKeydown)
    super.disconnectedCallback()
  }

  private get vmOrNull(): ValuesCardgameVm | null {
    try {
      return this.vm
    } catch {
      return null
    }
  }

  private readonly onKeydown = (e: KeyboardEvent): void => {
    const vm = this.vmOrNull
    const snap = vm?.game.value
    if (!vm || !snap) return
    // Never hijack a browser/OS chord or typing in a field (none here today, but the
    // listener is document-level, so stay a good citizen).
    if (e.ctrlKey || e.metaKey || e.altKey) return
    const target = e.target
    if (target instanceof HTMLElement && (target.isContentEditable || /^(?:INPUT|TEXTAREA|SELECT)$/.test(target.tagName))) return

    if (snap.phase === "reduce") {
      if (e.key === "ArrowLeft" || e.key === "1") {
        e.preventDefault()
        vm.chooseSide("a")
      } else if (e.key === "ArrowRight" || e.key === "2") {
        e.preventDefault()
        vm.chooseSide("b")
      } else if (e.key === "u" || e.key === "U") {
        e.preventDefault()
        vm.toggleUnsure()
      } else if (e.key === "Escape") {
        vm.setUnsure(false)
      }
      return
    }

    if (snap.phase === "confirm") {
      const onButton = e.target instanceof HTMLElement && e.target.closest("button")
      if (e.key === "Enter" && !onButton) {
        e.preventDefault()
        void vm.commit()
      } else if (e.key === "Escape" || e.key === "Backspace") {
        e.preventDefault()
        vm.backToSuperkraefte()
      }
      return
    }

    // Pick phases (refine / lebenswert / superkraefte): arrow keys rove tile focus.
    if (e.key === "ArrowRight" || e.key === "ArrowDown" || e.key === "ArrowLeft" || e.key === "ArrowUp") {
      const forward = e.key === "ArrowRight" || e.key === "ArrowDown"
      if (this.moveTileFocus(forward)) e.preventDefault()
    }
  }

  /** Move focus to the next/previous enabled `.vg-tile` within the grid that holds
   *  focus (or the first grid), wrapping at the ends. Returns whether it moved. */
  private moveTileFocus(forward: boolean): boolean {
    const active = document.activeElement
    const grid = active instanceof HTMLElement ? active.closest(".vg-grid") : null
    const scope: ParentNode = grid ?? this
    const tiles = [...scope.querySelectorAll<HTMLButtonElement>("button.vg-tile:not([disabled])")]
    if (tiles.length === 0) return false
    const idx = active instanceof HTMLButtonElement ? tiles.indexOf(active) : -1
    const next = idx === -1 ? (forward ? 0 : tiles.length - 1) : (idx + (forward ? 1 : -1) + tiles.length) % tiles.length
    tiles[next]?.focus()
    return true
  }

  // ── Shared card chrome ─────────────────────────────────────────────────────

  /**
   * A single value card FACE — a real playing card: colour-family frame, a
   * theme-aware illustration, and the value term set large in the serif. The user
   * reads only the term and consumes the image (operator directive); no definition,
   * rationale or family label rides on the face. Text always fits: `hyphens: auto`
   * + `overflow-wrap` + a container-query `clamp()` size (see the `.vg-card` CSS),
   * so even »Selbstverwirklichung« wraps instead of overflowing.
   *
   * The illustration is a CSS background layered over the always-present colour
   * field — a missing `public/values/<id>.<theme>.webp` simply doesn't paint (no
   * broken-image icon, the colour field stays). Both theme variants are handed to
   * CSS as custom properties; the stylesheet picks light/dark to match the app.
   */
  private cardFace(card: ValueCard): TemplateResult {
    const light = `/values/${card.cardId}.light.webp`
    const dark = `/values/${card.cardId}.dark.webp`
    const media = `--vg-img-l:url('${light}');--vg-img-d:url('${dark}')`
    return html`<span class="vg-card vg-fam-${card.colour}">
      <span class="vg-card-field" aria-hidden="true"></span>
      <span class="vg-card-illus" style=${media} aria-hidden="true"></span>
      <span class="vg-card-term" lang="de">${card.name}</span>
    </span>`
  }

  /** The persistent step path (Reduzieren → … → Bestätigen) — visible in every phase
   *  so a phase transition announces itself as the current step moves. */
  private stepPath(vm: ValuesCardgameVm, phase: GamePhase): TemplateResult {
    const currentIdx = STEP_PATH.findIndex((s) => s.phase === phase)
    return html`<ol class="vg-steps" aria-label=${vm.t("valuecard.steps.label")}>
      ${STEP_PATH.map((step, i) => {
        const state = i < currentIdx ? "done" : i === currentIdx ? "current" : "todo"
        return html`<li
          class=${classMap({ "vg-step": true, "is-done": state === "done", "is-current": state === "current" })}
          aria-current=${state === "current" ? "step" : nothing}
        >
          <span class="vg-step-dot" aria-hidden="true">${state === "done" ? "✓" : i + 1}</span>
          <span class="vg-step-label" lang="de">${vm.t(step.label)}</span>
        </li>`
      })}
    </ol>`
  }

  /** The one-line, warm guide for the current phase (what to do now, what's next).
   *  `aria-live` so a screen reader hears the change when the phase advances. */
  private guideLine(vm: ValuesCardgameVm, phase: GamePhase): TemplateResult {
    const key = `valuecard.guide.${phase}` as CareerKey
    // The reduce instruction is phone-only (operator §1): on a pointer device the big serif
    // question carries the intent, so this one-liner would just be noise. `vg-guide--touch`
    // hides it on wide/hover screens (CSS), keeping it for touch/narrow.
    const cls = classMap({ "vg-guide": true, "vg-guide--touch": phase === "reduce" })
    return html`<p class=${cls} role="status" aria-live="polite" lang="de">${vm.t(key)}</p>`
  }

  /**
   * The delayed support line beneath the question (operator §3): after {@link HINT_DELAY_MS} of
   * dwell on ONE unchanged pair, a small, muted »Lieber ein Leben voller X und ohne Y …« fades in
   * to help a stuck decision. Its space is ALWAYS reserved (the `<p>` stays in the DOM), so the
   * fade never shifts the cards. `aria-hidden` until visible so a screen reader isn't nagged.
   */
  private delayedHint(vm: ValuesCardgameVm, pair: { a: ValueCard; b: ValueCard }): TemplateResult {
    const on = vm.hintVisible.value
    const key: CareerKey = fullnessNeedsMit(pair.a.name, pair.b.name)
      ? "valuecard.reduce.delayedHintMit"
      : "valuecard.reduce.delayedHint"
    return html`<p
      class=${classMap({ "vg-delayed-hint": true, "is-visible": on })}
      role="status"
      aria-live="polite"
      aria-hidden=${on ? nothing : "true"}
      lang="de"
    >
      ${on ? vm.t(key, { x: pair.a.name, y: pair.b.name }) : nothing}
    </p>`
  }

  private colourLegend(vm: ValuesCardgameVm): TemplateResult {
    return html`<ul class="vg-legend" aria-label=${vm.t("valuecard.legend")}>
      ${VALUE_COLOUR_FAMILIES.map(
        (f) => html`<li class="vg-legend-item vg-fam-${f.id}">
          <span class="vg-swatch" aria-hidden="true"></span>
          <span class="vg-legend-word" lang="de">${vm.t(FAMILY_WORD[f.id])}</span>
          <span class="vg-legend-label" lang="de">${f.label}</span>
        </li>`,
      )}
    </ul>`
  }

  // ── Phase: reduce (pairwise »Was ist dir wichtiger?«) ──────────────────────
  private reducePhase(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    const pair = currentPair(snap)
    if (!pair) return html`<p class="surface-empty" lang="de">${vm.t("valuecard.reduce.empty")}</p>`
    const deck = snap.keep.length
    const pairsTotal = roundPairCount(snap)
    const pairsLeft = Math.max(0, pairsTotal - snap.pairIndex)
    // A SMOOTH within-phase fill: `reduceProgress` only steps at round boundaries
    // (keep is rewritten per round), which leaves the bar frozen for a whole round.
    // Each decided pair eliminates one card, so the live count is `keep − pairIndex`;
    // fill that against the whole 62→8 span so the bar advances every choice.
    const start = snap.order.length
    const live = Math.max(PYRAMID_SIZE, deck - snap.pairIndex)
    const progress = Math.min(1, Math.max(0, (start - live) / Math.max(1, start - PYRAMID_SIZE)))
    const armed = vm.unsureArmed.value
    // The teaching signal (operator §3): only the very first comparison — before ANY pick —
    // shows the explainer chrome. `pairIndex`/`round` advance on the first `chooseWinner`, so
    // `firstPair` flips false the moment the user has interacted once → the game turns calm.
    const firstPair = snap.round === 0 && snap.pairIndex === 0
    const miles = reduceMilestones(snap.order.length)
    return html`
      <div class=${classMap({ "vg-reduce": true, "is-unsure": armed, "is-first": firstPair })}>
        <!-- Reduction journey (62 › 31 › 16 › 8): a teaching flourish shown only on the first
             comparison — mid-game the progress bar + »Noch N Karten« carry the same signal calmly. -->
        ${firstPair
          ? html`<ol class="vg-journey" aria-hidden="true">
              ${miles.map(
                (m) => html`<li class=${classMap({ "vg-mile": true, "is-done": m > deck, "is-now": m === deck })}>${m}</li>`,
              )}
            </ol>`
          : nothing}
        <div
          class="vg-progress"
          role="progressbar"
          aria-label=${vm.t("valuecard.progress.label")}
          aria-valuemin="0"
          aria-valuemax="100"
          aria-valuenow=${Math.round(progress * 100)}
        >
          <span class="vg-progress-fill" style=${`--vg-progress:${progress}`}></span>
        </div>
        <p class="vg-round" lang="de">
          <span>${vm.t("valuecard.reduce.round", { n: snap.round + 1 })}</span>
          <span>${vm.t("valuecard.reduce.count", { n: deck })} · ${vm.t("valuecard.reduce.pairsLeft", { n: pairsLeft })}</span>
        </p>
        <p class="vg-reduce-prompt" lang="de">${vm.t("valuecard.reduce.prompt")}</p>
        <div class="vg-pair" role="group" aria-label=${vm.t("valuecard.reduce.prompt")}>
          <button
            class="vg-choice"
            data-act="choose"
            data-winner="a"
            aria-label=${vm.t("valuecard.reduce.choose", { name: pair.a.name })}
          >
            ${this.cardFace(pair.a)}
          </button>
          <span class="vg-vs" aria-hidden="true" lang="de">${vm.t("valuecard.reduce.vs")}</span>
          <button
            class="vg-choice"
            data-act="choose"
            data-winner="b"
            aria-label=${vm.t("valuecard.reduce.choose", { name: pair.b.name })}
          >
            ${this.cardFace(pair.b)}
          </button>
        </div>
        <div class="vg-reduce-actions">
          <button class="vg-unsure" data-act="toggle-unsure" aria-pressed=${armed ? "true" : "false"} lang="de">
            ${vm.t("valuecard.reduce.unsure")}
          </button>
        </div>
        ${armed
          ? html`<p class="vg-unsure-hint" role="status" aria-live="polite" lang="de">${vm.t("valuecard.reduce.unsureOn")}</p>`
          : nothing}
        ${firstPair ? html`<p class="vg-hint surface-empty" lang="de">${vm.t("valuecard.reduce.firstHint")}</p>` : nothing}
        <p class="vg-kbd surface-empty" lang="de">${vm.t("valuecard.kbd.reduce")}</p>
        <!-- The delayed support line sits LAST (operator §4): muted, small, at the very bottom
             so it never pushes the cards; its space is reserved so the fade-in never shifts layout. -->
        ${this.delayedHint(vm, pair)}
      </div>`
  }

  // ── Phase: refine (bounded rescue/swap) ────────────────────────────────────
  private refinePhase(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    const kept = keptCards(snap)
    // The promise »wir kommen auf deine Unsicheren zurück« lands HERE: the cards the user
    // explicitly marked »unsicher« during Reduzieren are THE exchange candidates. If they were
    // never unsure, say so honestly and only then fall back to the wider set-aside pile.
    const unsure = unsureCandidates(snap)
    const hasUnsure = unsure.length > 0
    const candidates = hasUnsure ? unsure : rescueCandidates(snap)
    const selected = vm.swapOut.value
    const canSwap = snap.rescueBudget > 0 && selected !== null
    return html`
      <div class="vg-refine">
        <h2 class="surface-section-title" lang="de">${vm.t("valuecard.refine.title")}</h2>
        <p class="surface-lead" lang="de">${vm.t("valuecard.refine.hint")}</p>
        <p class="vg-budget chip" lang="de">${vm.t("valuecard.refine.budget", { n: snap.rescueBudget })}</p>
        <ul class="vg-grid" role="list">
          ${kept.map(
            (c) => html`<li>
              <button
                class=${classMap({ "vg-tile": true, ["vg-fam-" + c.colour]: true, "is-selected": selected === c.cardId })}
                data-act="select-swap-out"
                data-card-id=${c.cardId}
                aria-pressed=${selected === c.cardId ? "true" : "false"}
                ?disabled=${snap.rescueBudget <= 0}
                lang="de"
              >${this.cardFace(c)}</button>
            </li>`,
          )}
        </ul>
        ${selected
          ? html`<div class="vg-rescue">
              ${hasUnsure
                ? html`<p class="eyebrow vg-rescue-unsure" lang="de">${vm.t("valuecard.refine.unsureTitle")}</p>`
                : html`<p class="vg-no-unsure surface-empty" lang="de">${vm.t("valuecard.refine.noUnsure")}</p>
                    <p class="eyebrow" lang="de">${vm.t("valuecard.refine.rescue")}</p>`}
              ${candidates.length > 0
                ? html`<ul class="vg-grid" role="list">
                    ${candidates.map(
                      (c) => html`<li>
                        <button class="vg-tile vg-fam-${c.colour} is-rescue" data-act="swap-in" data-card-id=${c.cardId} ?disabled=${!canSwap} lang="de">
                          ${this.cardFace(c)}
                        </button>
                      </li>`,
                    )}
                  </ul>`
                : html`<p class="surface-empty" lang="de">${vm.t("valuecard.refine.noneLeft")}</p>`}
            </div>`
          : nothing}
        <div class="vg-actions">
          <button class="primary" data-act="finish-refine" lang="de">${vm.t("valuecard.refine.done")}</button>
        </div>
        <p class="vg-kbd surface-empty" lang="de">${vm.t("valuecard.kbd.pick")}</p>
      </div>`
  }

  // ── Phase: pick 1 Lebenswert ───────────────────────────────────────────────
  private lebenswertPhase(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    return html`
      <div class="vg-pick">
        <h2 class="surface-section-title" lang="de">${vm.t("valuecard.lebenswert.title")}</h2>
        <p class="surface-lead" lang="de">${vm.t("valuecard.lebenswert.hint")}</p>
        <ul class="vg-grid" role="list">
          ${keptCards(snap).map(
            (c) => html`<li>
              <button
                class=${classMap({ "vg-tile": true, ["vg-fam-" + c.colour]: true, "is-crown": snap.lebenswert === c.cardId })}
                data-act="pick-lebenswert"
                data-card-id=${c.cardId}
                aria-pressed=${snap.lebenswert === c.cardId ? "true" : "false"}
                lang="de"
              >${this.cardFace(c)}</button>
            </li>`,
          )}
        </ul>
        <p class="vg-kbd surface-empty" lang="de">${vm.t("valuecard.kbd.pick")}</p>
      </div>`
  }

  // ── Phase: pick 3 Superkräfte ──────────────────────────────────────────────
  private superkraeftePhase(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    const pool = keptCards(snap).filter((c) => c.cardId !== snap.lebenswert)
    const chosen = snap.superkraefte.length
    const ready = chosen === PYRAMID_SHAPE.superkraefte
    return html`
      <div class="vg-pick">
        <h2 class="surface-section-title" lang="de">${vm.t("valuecard.superkraefte.title")}</h2>
        <p class="surface-lead" lang="de">${vm.t("valuecard.superkraefte.hint")}</p>
        <p class="vg-count chip" lang="de">${vm.t("valuecard.superkraefte.count", { n: chosen, total: PYRAMID_SHAPE.superkraefte })}</p>
        <ul class="vg-grid" role="list">
          ${pool.map((c) => {
            const on = snap.superkraefte.includes(c.cardId)
            const full = !on && ready
            return html`<li>
              <button
                class=${classMap({ "vg-tile": true, ["vg-fam-" + c.colour]: true, "is-super": on })}
                data-act="toggle-superkraft"
                data-card-id=${c.cardId}
                aria-pressed=${on ? "true" : "false"}
                ?disabled=${full}
                lang="de"
              >${this.cardFace(c)}</button>
            </li>`
          })}
        </ul>
        <div class="vg-actions">
          <button class="ghost" data-act="back-to-lebenswert" lang="de">${vm.t("valuecard.action.back")}</button>
          <button class="primary" data-act="confirm-superkraefte" ?disabled=${!ready} lang="de">${vm.t("valuecard.action.next")}</button>
        </div>
        <p class="vg-kbd surface-empty" lang="de">${vm.t("valuecard.kbd.pick")}</p>
      </div>`
  }

  /** The compact 1·3·4 pyramid — Lebenswert crown → Superkräfte → Basiswerte. */
  private pyramidView(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    const card = (id: string): ValueCard | undefined => valueCardById(id)
    const superkraefte = snap.superkraefte.map(card).filter((c): c is ValueCard => Boolean(c))
    const basiswerte = basiswerteIds(snap).map(card).filter((c): c is ValueCard => Boolean(c))
    const crown = snap.lebenswert ? card(snap.lebenswert) : undefined
    const tierRow = (labelKey: CareerKey, cards: ValueCard[]) => html`
      <div class="vg-tier">
        <span class="vg-tier-label eyebrow" lang="de">${vm.t(labelKey)}</span>
        <ul class="vg-tier-cards" role="list">
          ${cards.map((c) => html`<li class="vg-tier-card vg-fam-${c.colour}">${this.cardFace(c)}</li>`)}
        </ul>
      </div>`
    return html`
      <div class="vg-pyramid">
        ${crown ? tierRow("valuecard.tier.lebenswert", [crown]) : nothing}
        ${tierRow("valuecard.tier.superkraefte", superkraefte)}
        ${tierRow("valuecard.tier.basiswerte", basiswerte)}
      </div>`
  }

  // ── Phase: confirm + commit ────────────────────────────────────────────────
  private confirmPhase(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    return html`
      <div class="vg-confirm">
        <h2 class="surface-section-title" lang="de">${vm.t("valuecard.confirm.title")}</h2>
        <p class="surface-lead" lang="de">${vm.t("valuecard.confirm.hint")}</p>
        ${this.pyramidView(vm, snap)}
        <div class="vg-actions">
          <button class="ghost" data-act="back-to-superkraefte" lang="de">${vm.t("valuecard.action.back")}</button>
          <button class="primary" data-act="commit" ?disabled=${vm.busy.value} lang="de">
            ${vm.busy.value ? vm.t("valuecard.confirm.committing") : vm.t("valuecard.confirm.commit")}
          </button>
        </div>
      </div>`
  }

  /** The finished pyramid, read from the server (a prior committed run). */
  private donePyramid(vm: ValuesCardgameVm, pyramid: ValuePyramid): TemplateResult {
    const byTier = (tier: string) =>
      pyramid.slots.filter((s) => s.tier === tier).map((s) => valueCardById(s.cardId)).filter((c): c is ValueCard => Boolean(c))
    const tierRow = (labelKey: CareerKey, cards: ValueCard[]) => html`
      <div class="vg-tier">
        <span class="vg-tier-label eyebrow" lang="de">${vm.t(labelKey)}</span>
        <ul class="vg-tier-cards" role="list">
          ${cards.map((c) => html`<li class="vg-tier-card vg-fam-${c.colour}">${this.cardFace(c)}</li>`)}
        </ul>
      </div>`
    return html`
      <div class="vg-done">
        <h2 class="surface-section-title" lang="de">${vm.t("valuecard.done.title")}</h2>
        <p class="surface-lead" lang="de">${vm.t("valuecard.done.hint")}</p>
        <div class="vg-pyramid">
          ${tierRow("valuecard.tier.lebenswert", byTier("Lebenswert"))}
          ${tierRow("valuecard.tier.superkraefte", byTier("Superkräfte"))}
          ${tierRow("valuecard.tier.basiswerte", byTier("Basiswerte"))}
        </div>
        <div class="vg-actions">
          <button class="ghost" data-act="start-new" lang="de">${vm.t("valuecard.done.restart")}</button>
        </div>
        ${this.goldenNext(vm)}
      </div>`
  }

  /** The golden-path forward affordance (D4): the pyramid is committed here → onward to the interview. */
  private goldenNext(vm: ValuesCardgameVm): TemplateResult | typeof nothing {
    const decl = spineDecl("career-values-cardgame")
    if (!decl) return nothing
    return nextStep(vm.t("next.eyebrow"), toNextStep(decl, vm.t(decl.labelKey)))
  }

  private intro(vm: ValuesCardgameVm): TemplateResult {
    return html`
      <div class="vg-intro">
        <p class="surface-lead" lang="de">${vm.t("valuecard.intro.lede")}</p>
        ${this.colourLegend(vm)}
        <div class="vg-actions">
          <button class="primary" data-act="start-new" lang="de">${vm.t("valuecard.intro.start")}</button>
        </div>
      </div>`
  }

  /** The active game: the persistent step path + a per-phase guide, then the phase. */
  private activeGame(vm: ValuesCardgameVm, snap: ValueGameSnapshot): TemplateResult {
    const phaseBody = (): TemplateResult => {
      switch (snap.phase) {
        case "reduce":
          return this.reducePhase(vm, snap)
        case "refine":
          return this.refinePhase(vm, snap)
        case "lebenswert":
          return this.lebenswertPhase(vm, snap)
        case "superkraefte":
          return this.superkraeftePhase(vm, snap)
        case "confirm":
          return this.confirmPhase(vm, snap)
      }
    }
    // Reduce carries its own state-gated teaching (firstHint on the very first pair only),
    // so the always-on phase guide is redundant there — keep it for the later phases where
    // the »was kommt als Nächstes« line genuinely orients the user.
    return html`
      <div class="vg-game">
        ${this.stepPath(vm, snap.phase)}
        ${snap.phase === "reduce" ? nothing : this.guideLine(vm, snap.phase)}
        ${phaseBody()}
      </div>`
  }

  private body(vm: ValuesCardgameVm): TemplateResult {
    const snap = vm.game.value
    if (snap) return this.activeGame(vm, snap)
    const view = vm.view.value
    if (view?.pyramid) return this.donePyramid(vm, view.pyramid)
    return this.intro(vm)
  }

  protected render(vm: ValuesCardgameVm): TemplateResult {
    const rootUrn = (this.urn ?? "urn:career:workshop:values") as Urn
    const err = vm.error.value
    return html`
      <section class="surface values-cardgame" urn=${rootUrn} aria-labelledby="vg-title">
        <p class="eyebrow" lang="de">${vm.t("valuecard.kicker")}</p>
        <h1 id="vg-title" class="surface-title" lang="de">${vm.t("surface.valuesCardgame.title")}</h1>
        ${err ? html`<p class="surface-error" role="alert" lang="de">${vm.t("review.error", { message: err })}</p>` : nothing}
        ${vm.loaded.value ? this.body(vm) : nothing}
      </section>`
  }

  protected override actions(vm: ValuesCardgameVm): ActionMap {
    return {
      "start-new": () => vm.startNew(),
      choose: ({ winner }) => vm.chooseSide(winner === "b" ? "b" : "a"),
      "toggle-unsure": () => vm.toggleUnsure(),
      "select-swap-out": ({ cardId }) => {
        if (cardId) vm.selectSwapOut(cardId)
      },
      "swap-in": ({ cardId }) => {
        if (cardId) vm.swapIn(cardId)
      },
      "finish-refine": () => vm.finishRefine(),
      "pick-lebenswert": ({ cardId }) => {
        if (cardId) vm.pickLebenswert(cardId)
      },
      "toggle-superkraft": ({ cardId }) => {
        if (cardId) vm.toggleSuperkraft(cardId)
      },
      "back-to-lebenswert": () => vm.backToLebenswert(),
      "confirm-superkraefte": () => vm.confirmSuperkraefte(),
      "back-to-superkraefte": () => vm.backToSuperkraefte(),
      commit: () => void vm.commit(),
      nav: ({ url }, event) => {
        event.preventDefault()
        if (url) vm.go(url)
      },
    }
  }
}
